// ---------------------------------------------------------------------------
// B5 — HER MOSES WORD-MAP APPROVAL LIST.
//
// Her order: *deliver the proposal as an APPROVAL LIST on her dashboard (approve / edit / skip per
// mapping, the review-ledger pattern). NOTHING ships into delivery without her tap per mapping.*
//
// **HOOKLESS ON PURPOSE, SO A GATE CALLS IT** (R7/R8). Every state a member of this house could ever
// meet it in is a prop, so the four-state read, the reach sentence and the three taps are RUN rather
// than grepped out of an eleven-thousand-line shell.
// ---------------------------------------------------------------------------
import { MOSES_MAP_ACTIONS } from "../mosesWordMap.js";

export type WordMapRow = {
  id: string;
  term: string;
  tag: string;
  figure: number | null;
  entry: string;
  why: string;
  askedTimes: number;
  state: string;
  reach: string;
  reachNote: string;
};

export type WordMapProps = {
  // >>> FOUR STATES, NEVER TWO (R18/R65). `undefined` is a read still in flight and HOLDS STILL;
  // `null` is a read that ARRIVED AND COULD NOT BE MADE and says so; an empty list is an ANSWER.
  // Collapsing the first two puts "there is nothing to approve" on the screen of an owner whose
  // dashboard could not reach the database — which on an approval list reads as work already done.
  rows?: WordMapRow[] | null;
  busyId?: string;
  onDecide: (id: string, action: string, tag?: string) => void;
  deliverableTags?: string[];
};

export function OwnerWordMap({ rows, busyId = "", onDecide, deliverableTags = [] }: WordMapProps) {
  if (rows === undefined) return <p className="owner-note">Reading the word map...</p>;
  if (rows === null) return <p className="owner-note owner-note--warn">The word map could not be read just now.</p>;
  if (!rows.length) return <p className="owner-note">Nothing is waiting for your word here.</p>;

  const waiting = rows.filter((r) => r.state === "proposed").length;
  return (
    <div className="wordmap" data-word-map>
      <p className="owner-note">
        {waiting > 0
          ? `${waiting} mapping${waiting === 1 ? "" : "s"} waiting for your word. Nothing binds until you approve it.`
          : "Every mapping has your word on it."}
      </p>
      {rows.map((r) => (
        <article key={r.id} className={`wordmap__row wordmap__row--${r.state}`} data-word-map-row={r.id} data-state={r.state}>
          <header className="wordmap__head">
            <span className="wordmap__term">&ldquo;{r.term}&rdquo;</span>
            <span className="wordmap__arrow" aria-hidden="true">-&gt;</span>
            <span className="wordmap__tag">{r.tag}</span>
            {r.askedTimes > 0 ? (
              <span className="wordmap__asked">asked {r.askedTimes} time{r.askedTimes === 1 ? "" : "s"}</span>
            ) : null}
          </header>
          {/* THE JUSTIFICATION IS HER BOOK'S OWN SENTENCE, VERBATIM (her clause: WITH the passage
              citations). Nothing here is composed, so she is judging her own words. */}
          <blockquote className="wordmap__why">
            <span className="wordmap__entry">{r.entry}{r.figure ? ` (Fig. ${r.figure})` : ""}</span>
            <span className="wordmap__passage">{r.why}</span>
          </blockquote>
          {/* R17/R160 — WHAT AN APPROVAL REALLY BUYS, SAID BEFORE SHE TAPS. A mapping onto a tag whose
              every entry is awaiting a plate changes a member's refusal rather than delivering her a
              working, and that is the difference between an approval list and a wish list. */}
          <p className={`wordmap__reach wordmap__reach--${r.reach}`}>{r.reachNote}</p>
          <div className="wordmap__acts">
            {MOSES_MAP_ACTIONS.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`button button--small${a.key === "approve" ? " button--primary" : ""}`}
                disabled={busyId === r.id}
                onClick={() => onDecide(r.id, a.key)}
              >
                {a.label}
              </button>
            ))}
            {r.state !== "proposed" ? (
              <span className="wordmap__decided">{r.state === "approved" ? "Approved" : "Skipped"}</span>
            ) : null}
          </div>
        </article>
      ))}
      {deliverableTags.length ? (
        <p className="owner-note wordmap__legend">
          Seals a member can receive today are filed under: {deliverableTags.join(", ")}.
        </p>
      ) : null}
    </div>
  );
}
